import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import PropTypes from "prop-types";
import SvgIcon from "../../../../parts/SvgIcon";
import Modal from "../../../../parts/modal-new";
import OptionToggler from "../../../../parts/OptionToggler";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import { getDateToDisplay } from "../../../../utils/nutrition-helpers";

const CopyMealPlanFromTemplateModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    activeUserToRequest,
    mealPlanTemplates = [],
    query,
    assignedMealPlans = [],
    allAthletesFromOrganization = [],
    activeUser: { athletes = [] },
  } = locals;
  const { referAsClients } = activeUserToRequest;
  const athleteLabel = referAsClients ? t("editMealPlan.copyMealPlanFromTemplateModal.selectClient") : t("editMealPlan.copyMealPlanFromTemplateModal.selectAthlete");
  const { mealPlan = "", week = 1 } = query;

  const [state, setState] = useState({
    selectedOption: "template",
    selectedWeek: null,
    selectedAthlete: null,
    selectedPlan: null,
  });

  const { selectedOption, selectedWeek, selectedAthlete, selectedPlan } = state;

  const togglerOptions = [
    { value: "template", label: t("editMealPlan.copyMealPlanFromTemplateModal.fromTemplate") },
    {
      value: "athlete",
      label: referAsClients ? t("editMealPlan.copyMealPlanFromTemplateModal.fromAnotherClient") : t("editMealPlan.copyMealPlanFromTemplateModal.fromAnotherAthlete"),
    },
  ];

  const filteredMealPlans = assignedMealPlans.filter(
    (plan) => plan._id !== mealPlan
  );

  const mealPlanOptions = mealPlanTemplates
    .filter((plan) => plan._id !== mealPlan)
    .map(({ _id, title, weeks = [] }) => ({
      value: _id,
      label: title,
      weeks,
    }));

  const athletesOptions = useMemo(() => {
    const assignedAthleteIds = new Set(
      filteredMealPlans.map(({ athleteId }) => athleteId)
    );

    const activeAthletes = athletes
      .filter(
        ({ status, _id }) => !status?.inactive && assignedAthleteIds.has(_id)
      )
      .map(({ _id, name, avatar }) => ({
        value: _id,
        label: name,
        avatar: avatar,
      }));

    const activeOrgAthletes = allAthletesFromOrganization
      .filter(
        ({ athleteStatus, athleteId }) =>
          !athleteStatus?.inactive && assignedAthleteIds.has(athleteId)
      )
      .map(({ athleteId, athleteName, athleteAvatar }) => ({
        value: athleteId,
        label: athleteName,
        avatar: athleteAvatar,
      }));

    const uniqueAthletes = new Map(
      [...activeAthletes, ...activeOrgAthletes].map((item) => [
        item.value,
        item,
      ])
    );

    return Array.from(uniqueAthletes.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [athletes, allAthletesFromOrganization, assignedMealPlans]);
  const searchOptions =
    selectedOption === "template"
      ? mealPlanOptions
      : selectedOption === "athlete" && selectedAthlete
      ? filteredMealPlans
          .filter(({ athleteId }) => athleteId === selectedAthlete.value)
          .map(
            ({
              _id: mealPlanId,
              utcStartDate,
              utcEndDate,
              weeks: mealPlanWeeks = [],
            }) => ({
              value: mealPlanId,
              label: `${getDateToDisplay(utcStartDate)} - ${getDateToDisplay(
                utcEndDate
              )}`,
              weeks: mealPlanWeeks,
            })
          )
      : [];

  /**
   * Functio to update the state
   * @param {*} newValues
   */
  const updateState = (newValues) => {
    setState((prevState) => ({ ...prevState, ...newValues }));
  };

  /**
   * Handle the selected week
   * @param {*} index
   */
  const handleSelectionChange = (index) => {
    updateState({ selectedWeek: index });
  };

  /**
   * Send the information to the api
   */
  const handleCopy = () => {
    const url = `/actions/meal-planner/copy-to-from-meal-template`;

    const body = {
      fromPlanId: selectedPlan?.value,
      handle: "to",
      fromSelectedWeeks: [selectedWeek],
      toPlans: {
        [mealPlan]: [week - 1],
      },
    };
    submit(url, {
      method: "POST",
      body,
    });
  };

  return (
    <Modal
      className="fetch-modal copy-meal-plan-from"
      title={t("editMealPlan.copyMealPlanFromTemplateModal.title")}
    >
      <div className="body">
        <OptionToggler
          name="fetchType"
          options={togglerOptions}
          defaultValue={selectedOption}
          onSelectFn={(value) =>
            updateState({
              selectedOption: value,
              selectedWeek: null,
              selectedAthlete: null,
              selectedPlan: null,
            })
          }
        />
        {selectedOption === "athlete" && (
          <>
            <label>{athleteLabel} to copy from</label>

            <SearchDropdown
              name="athletes"
              options={athletesOptions}
              handleChange={(value) =>
                updateState({
                  selectedAthlete: value,
                  selectedPlan: null,
                  selectedWeek: null,
                })
              }
              value={selectedAthlete?.label}
              valueKey="label"
              placeholder={athleteLabel}
            />
          </>
        )}
        {selectedOption === "template" ||
        (selectedOption === "athlete" && selectedAthlete) ? (
          <>
            <label>{t("editMealPlan.copyMealPlanFromTemplateModal.selectMealPlan")}</label>

            <SearchDropdown
              name="plans"
              options={searchOptions}
              handleChange={(value) =>
                updateState({ selectedPlan: value, selectedWeek: null })
              }
              value={selectedPlan?.label}
              valueKey="label"
              placeholder={t("editMealPlan.copyMealPlanFromTemplateModal.selectMealPlanPlaceholder")}
            />
          </>
        ) : null}
        {selectedPlan && (
          <>
            <div className="weeks-container">
              <label>{t("editMealPlan.copyMealPlanFromTemplateModal.selectWeek")}</label>
              <div className="week-list">
                <ul>
                  {selectedPlan?.weeks.map(({}, index) => (
                    <li key={`${selectedPlan?._id}-${index}`}>
                      <SvgIcon
                        onClick={() => handleSelectionChange(index)}
                        src={`/img/new-icons/${
                          selectedWeek === index
                            ? "checked-square.svg"
                            : "unchecked-square.svg"
                        }`}
                        noFilterOnLight={selectedWeek === index}
                      />
                      <span>{t("editMealPlan.copyMealPlanFromTemplateModal.week", { n: index + 1 })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="group fetch-program-summary">
              <p className="chip chip warning">
                {t("editMealPlan.copyMealPlanFromTemplateModal.overwriteWarning")}
              </p>
            </div>
          </>
        )}
      </div>

      <footer>
        <a
          role="button"
          data-button-type="secondary"
          data-action="cancel"
          href="/actions/refresh?modal=&sourceOwner=&week="
        >
          {t("editMealPlan.copyMealPlanFromTemplateModal.cancel")}
        </a>
        <button
          type="submit"
          data-button-type="primary"
          disabled={selectedWeek === null}
          onClick={handleCopy}
        >
          {t("editMealPlan.copyMealPlanFromTemplateModal.addMealPlan")}
        </button>
      </footer>
    </Modal>
  );
};

CopyMealPlanFromTemplateModal.propTypes = {
  locals: PropTypes.object,
};

export default CopyMealPlanFromTemplateModal;
